/**
 * E-validation kiosk (PR6, re-skinned in slice 4c) — "tablet, no scan".
 *
 * A front-desk tablet left signed-in to the tenant portal. Staff look a
 * customer up by PHONE or PLATE — whichever they have on hand (slice 4 added
 * the phone path) — and the worker finds the driver's active PAID session and
 * refunds the discount to the card they already paid with. The driver scans
 * nothing. Designed for touch: large targets, a segmented lookup toggle, and
 * full-width result banners readable across a counter.
 *
 * Money shape (two-step, 2026-08-27; registered visitors auto-apply,
 * owner decision 2026-10-01): the lookup is a preview that moves no money and
 * reports what's actually parked. A REGISTERED visitor is one whose lookup
 * finds an active paid session (a `found` preview carrying its `sessionRef`),
 * which is exactly the demo's `hasSession` branch ("Applied automatically").
 * For them the apply follows the preview with no confirm tap, but it is still
 * the second call: it carries the reviewed lookup and that preview's
 * sessionRef, and the server refuses unless it resolves the same session under
 * the same program. A `found` that cannot be bound (no sessionRef) keeps the
 * manual Confirm row. Arming a confirm BEFORE the lookup (the old shape) let
 * the tablet promise "Refund 12 hours free to (706) 699-4401?" to a number with
 * no session at all, which is what a walk-up who hasn't parked yet looks like. A
 * phone matching 2+ cars comes back as a bare count (`multiple_sessions`,
 * never a session list on a shared iPad) and staff re-look-up by plate.
 * No active session → registration invite: the visitor scans the posted
 * Parkify code, or staff text them a link that pre-applies this validation.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import type { TenantSession } from '../lib/session'
import {
  listValidations, evalidate, sendInvite, startFreeSession,
  type Validation, type EvalidateResult, type TenantInfo, type KioskFreeStartResult,
} from '../lib/api'
import { labelStyle as baseLabel, screenTitle, buttonRadius } from '../ui'
import { generateBrandedQrSvg } from '../lib/qrBrand'
import { formatValidationHours } from '../lib/validationHours'
import { issuableOffers } from '../lib/issuableOffers'
import { isOfferUsedUp, usedUpLabel } from '../lib/offerEligibility'
import { allowanceState, refusalOfError, refusalOfResult, type KioskRefusal } from '../lib/kioskRefusal'

/* Same treatment as every other screen's label; this one keeps its own
   bottom margin (and Register its `display`), which is spacing, not type. */
const labelStyle: CSSProperties = { ...baseLabel,
  marginBottom: 6, }

type Method = 'phone' | 'plate'

/**
 * One lookup, frozen at the moment it was sent (TEN-02). A result belongs to
 * the lookup that produced it, and so does everything it arms: the refund
 * confirm, the free start and the invite. They read this, never the live
 * inputs, which may have been edited since.
 */
type LookupIdentity = { code: string; method: Method; plate: string; phone: string }

type Outcome =
  // `auto`: the apply followed the preview with no confirm tap (registered visitor).
  | { kind: 'result'; result: EvalidateResult; lookup: LookupIdentity; auto?: boolean }
  | { kind: 'error'; message: string; refusal: KioskRefusal | null }

const lookupBody = (l: LookupIdentity): { phone: string } | { plate: string } =>
  l.method === 'phone' ? { phone: l.phone } : { plate: l.plate }

// Outcomes where the car was found/handled → clear the typed lookup so the next
// walk-up at this always-signed-in tablet can't see or re-submit it. A
// no_session/rejected/multiple miss keeps the input so the clerk can fix a
// typo + retry (or switch tabs).
const CLEAR_LOOKUP_ON = new Set<EvalidateResult['status']>(['validated', 'already_validated', 'already_free', 'time_appended'])
// Idle auto-reset: a result/lookup left on the counter clears itself so the
// next customer starts fresh (privacy + no accidental re-tap). Any typing in
// the invite block re-arms the timer so it can't wipe a half-entered number.
const IDLE_RESET_MS = 18_000
// Plate input mask — strip everything but A–Z/0–9 so the kiosk read matches the
// stored (stripped) plate; the server normalizes too, this is just instant feedback.
const maskPlate = (s: string): string => s.toUpperCase().replace(/[^A-Z0-9]/g, '')
// Progressive US phone formatting — mirrors the demo's kiosk field: digits in,
// "(555) 123-4567" out. Server compares last-10, so formatting is display-only.
const fmtPhone = (s: string): string => {
  const d = s.replace(/\D/g, '').slice(0, 10)
  if (d.length <= 3) return d
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}
const clockLabel = (iso?: string): string => {
  if (!iso) return ''
  const at = new Date(iso)
  if (isNaN(at.getTime())) return ''
  return at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

// Status → banner styling. `tone` drives the color block; `ok` flags the one
// happy path (a refund was actually issued) for the prominent amount line.
const TONE: Record<EvalidateResult['status'], { bg: string; border: string; color: string; icon: string; ok?: boolean }> = {
  found:              { bg: 'rgba(33,63,153,0.10)',  border: 'rgba(33,63,153,0.4)',  color: '#213F99', icon: '🚗' },
  validated:          { bg: 'rgba(98,197,132,0.14)', border: 'rgba(98,197,132,0.5)', color: '#15803d', icon: '✓', ok: true },
  time_appended:      { bg: 'rgba(98,197,132,0.14)', border: 'rgba(98,197,132,0.5)', color: '#15803d', icon: '✓', ok: true },
  no_time_added:      { bg: 'rgba(241,101,33,0.12)', border: 'rgba(241,101,33,0.45)', color: '#b45309', icon: '!' },
  fully_covered:      { bg: 'rgba(33,63,153,0.10)',  border: 'rgba(33,63,153,0.4)',  color: '#213F99', icon: 'ℹ' },
  already_validated:  { bg: 'rgba(33,63,153,0.10)',  border: 'rgba(33,63,153,0.4)',  color: '#213F99', icon: 'ℹ' },
  already_free:       { bg: 'rgba(33,63,153,0.10)',  border: 'rgba(33,63,153,0.4)',  color: '#213F99', icon: 'ℹ' },
  no_session:         { bg: 'rgba(241,101,33,0.12)', border: 'rgba(241,101,33,0.45)', color: '#b45309', icon: '!' },
  multiple_sessions:  { bg: 'rgba(241,101,33,0.12)', border: 'rgba(241,101,33,0.45)', color: '#b45309', icon: '2+' },
  rejected:           { bg: 'rgba(237,28,36,0.10)',  border: 'rgba(237,28,36,0.4)',  color: '#b91c1c', icon: '✕' },
  inactive:           { bg: 'var(--color-gray-100)', border: 'var(--color-gray-300)', color: 'var(--color-gray-600)', icon: '–' },
}

export function Validate({ session, tenant, initialCode = '', hideMonetaryValues = false }: {
  session: TenantSession
  tenant: TenantInfo
  /** Validation-only lots have no paid product, so dollar/refund UI is absent. */
  hideMonetaryValues?: boolean
  /**
   * Offer to open on, from a tablet link's `?code=` (the link generated for one
   * validation should land on that validation). Advisory: honored only if it's
   * actually one of THIS tenant's active, issuable offers, so an unknown or
   * other-tenant code silently falls back to the normal default.
   */
  initialCode?: string
}) {
  const [offers, setOffers] = useState<Validation[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [code, setCode] = useState('')
  const [method, setMethod] = useState<Method>('phone')
  const [plate, setPlate] = useState('')
  const [phone, setPhone] = useState('')
  // 'lookup' while a preview is out, 'confirm' while a refund is. Different on
  // purpose: an edit abandons a pending lookup, but the inputs lock while money
  // is moving, because that answer must always reach the screen.
  const [busy, setBusy] = useState<null | 'lookup' | 'confirm'>(null)
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  // Bumped by every change to WHAT is being looked up (offer, method, plate,
  // phone, reset, unmount). A preview whose generation is no longer current
  // describes inputs that are gone, so its answer is dropped: shown nowhere,
  // arming nothing. This is the TEN-02 race: an old lookup used to land after
  // an edit and arm a confirm that named the old car.
  const lookupGen = useRef(0)
  // Bumped by invite-block activity so the idle reset can't wipe mid-typing.
  const [activityTick, setActivityTick] = useState(0)

  const load = async () => {
    setLoading(true); setErr('')
    try {
      const all = await listValidations(session.token)
      const ev = issuableOffers(all)
      setOffers(ev)
      // Default to the first offer for ANY count — with 2+, the controlled
      // <select> otherwise DISPLAYS the first option while `code` stays '',
      // dead-locking the submit button until staff re-pick what already looks
      // selected (pre-existing; surfaced by the first multi-offer tenant).
      // A tablet link's `?code=` wins when it names a real offer here — that's
      // what makes a per-validation link feel per-validation. Validated against
      // the fetched list, so it can only ever select something this tenant
      // already has.
      // A used-up program (inventory spent, or its spend limit or prepaid block
      // reported reached) is never auto-selected: the same rule the header
      // picker and the cards use. Unknown, on either axis, stays eligible.
      const open = ev.filter(o => !isOfferUsedUp(o))
      const preferred = open.find(o => o.code === initialCode.trim().toUpperCase())?.code
      setCode(prev => preferred ?? (prev && open.some(o => o.code === prev) ? prev : open[0]?.code ?? ''))
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'load failed'); console.error('[tenant] kiosk load', e)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { void load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const selected = useMemo(() => offers.find(o => o.code === code) ?? null, [offers, code])
  const phoneDigits = phone.replace(/\D/g, '')
  const lookupReady = method === 'phone' ? phoneDigits.length === 10 : plate.length >= 2
  // A spent allowance ends the lookup before it starts: the answer is already
  // known, and a tap that could only be refused later wastes the clerk's time.
  // Unknown is NOT spent (see lib/kioskRefusal.ts), so it never lands here.
  const allowance = allowanceState(selected)
  const canSubmit = !!code && lookupReady && !busy && allowance !== 'exhausted'

  const invalidateLookup = () => {
    lookupGen.current += 1
    setBusy(b => (b === 'lookup' ? null : b))
  }
  useEffect(() => () => { lookupGen.current += 1 }, [])

  // The lookup half: the preview itself moves no money. The kiosk ALWAYS
  // previews first, and the apply is reachable only from a `found` preview, so
  // money never moves before a car is on screen. A registered visitor (a bound
  // `found`) is applied straight away; anything else stops for staff.
  const lookUp = async () => {
    if (!canSubmit) return
    const lookup: LookupIdentity = { code, method, plate, phone: phoneDigits }
    const gen = ++lookupGen.current
    setBusy('lookup'); setOutcome(null)
    try {
      const result = await evalidate(session.token, lookup.code, lookupBody(lookup), { preview: true })
      if (gen !== lookupGen.current) return
      // Registered visitor: apply the reviewed session now. busy flips to
      // 'confirm' in the same tick the answer is accepted, so the inputs lock
      // before anything else can change what was reviewed.
      if (result.status === 'found' && result.sessionRef && !hideMonetaryValues) {
        await applyReviewed(lookup, result, true)
        return
      }
      setOutcome({ kind: 'result', result, lookup })
      if (CLEAR_LOOKUP_ON.has(result.status)) { setPlate(''); setPhone('') }
    } catch (e) {
      if (gen !== lookupGen.current) return
      setOutcome({ kind: 'error', message: e instanceof Error ? e.message : 'Something went wrong. Try again.', refusal: refusalOfError(e) })
    } finally {
      if (gen === lookupGen.current) setBusy(null)
    }
  }

  // The apply. Sends the REVIEWED lookup, not the live inputs, plus the
  // preview's sessionRef, so the server refuses unless it resolves the very
  // session that was on screen (a new session on the same plate included).
  // Its answer is always shown: by the time it returns, money may have moved.
  // Reached from the manual Confirm row, or automatically for a registered visitor.
  const applyReviewed = async (lookup: LookupIdentity, reviewed: EvalidateResult, auto: boolean) => {
    setBusy('confirm')
    try {
      const result = await evalidate(session.token, lookup.code, lookupBody(lookup), { expectedSession: reviewed.sessionRef })
      setOutcome({ kind: 'result', result, lookup, auto })
      if (CLEAR_LOOKUP_ON.has(result.status)) { setPlate(''); setPhone('') }
    } catch (e) {
      setOutcome({ kind: 'error', message: e instanceof Error ? e.message : 'Something went wrong. Try again.', refusal: refusalOfError(e) })
    } finally {
      setBusy(null)
    }
  }

  const confirmRefund = async () => {
    if (busy || outcome?.kind !== 'result' || outcome.result.status !== 'found') return
    await applyReviewed(outcome.lookup, outcome.result, false)
  }

  const reset = () => { invalidateLookup(); setPlate(''); setPhone(''); setOutcome(null) }
  const switchMethod = (m: Method) => {
    if (m !== method) invalidateLookup()
    setMethod(m)
    if (outcome) setOutcome(null)
  }
  const locked = busy === 'confirm'

  // Auto-reset the counter after a result has been on screen a while, so the
  // next customer doesn't inherit the prior driver's lookup or banner. Invite
  // activity re-arms it (activityTick) so a half-typed number isn't wiped.
  // Never mid-refund: a reset there would hide the answer to a refund in flight.
  useEffect(() => {
    if (!outcome || busy === 'confirm') return
    const t = setTimeout(reset, IDLE_RESET_MS)
    return () => clearTimeout(t)
  }, [outcome, activityTick, busy])

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-gray-600)' }}>Loading…</div>

  // A failed load leaves offers empty — show the load-failure block, not the
  // "create an offer" empty state, so a front-desk worker isn't sent to fix the
  // wrong thing when the real problem is the fetch.
  if (err) {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <LoadError message={err} onRetry={() => void load()} />
      </div>
    )
  }

  if (offers.length === 0) {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <Card>
          <StaffHeader tenant={tenant} />
          <h2 style={{ ...screenTitle, marginBottom: 8 }}>No validations yet</h2>
          <p style={{ fontSize: 14, color: 'var(--color-gray-600)', lineHeight: 1.6, margin: 0 }}>
            Your Parkify contact sets up validation programs. Once one is added, every active
            validation appears here, whichever delivery method it uses.
          </p>
        </Card>
      </div>
    )
  }

  const result = outcome?.kind === 'result' ? outcome.result : null
  // The lookup the on-screen result answers, and that lookup's offer.
  const reviewed = outcome?.kind === 'result' ? outcome.lookup : null
  const reviewedOffer = reviewed ? offers.find(o => o.code === reviewed.code) ?? null : null

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <Card>
        <StaffHeader tenant={tenant} />
        <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px' }}>Validate a customer's parking</h2>
        <p style={{ fontSize: 13, color: 'var(--color-gray-600)', margin: '0 0 18px' }}>
          Look them up by phone or plate — whichever they have on hand. {hideMonetaryValues
            ? "If they haven't started parking yet, "
            : "If they're parked, the discount is refunded to the card they paid with. If they haven't started parking yet, "}
          {/* The second half has to name the move this offer actually affords:
              promising "text them a link" on a grant that starts the session
              outright undersells it, and promising "start it for them" on a
              flat/percent discount is a move the server will refuse. */}
          {canStartFree(selected)
            ? 'you can start it for them right here — all you need is their plate.'
            : 'you can text them a link that applies it for them.'}
        </p>

        {offers.length > 1 && (
          <div style={{ marginBottom: 18 }}>
            <div style={labelStyle}>Offer</div>
            <select value={code} disabled={locked} onChange={e => { invalidateLookup(); setCode(e.target.value); setOutcome(null) }} style={{ ...bigInput, appearance: 'auto' }}>
              {offers.map(o => (
                <option key={o.code} value={o.code} disabled={isOfferUsedUp(o)}>
                  {o.name || o.code} — {validationDisplayLabel(o, hideMonetaryValues)}{isOfferUsedUp(o) ? ` (${usedUpLabel(o).toLowerCase()})` : ''}
                </option>
              ))}
            </select>
            {selected && <ScheduleNotice offer={selected} />}
          </div>
        )}
        {offers.length === 1 && selected && (
          <div style={{ marginBottom: 18, fontSize: 14 }}>
            <span style={{ color: 'var(--color-gray-600)' }}>Offer: </span>
            <b>{selected.name || selected.code}</b>
            <span style={{ color: 'var(--color-gray-600)' }}> — {validationDisplayLabel(selected, hideMonetaryValues)}</span>
            <ScheduleNotice offer={selected} />
          </div>
        )}

        {/* Every program spent (nothing is auto-selected: #3398) reads the same as a spent
            selection: one panel, one re-check, one guest-pay line. */}
        {allowance === 'exhausted' || (offers.length > 0 && !selected && offers.every(isOfferUsedUp)) ? (
          <div style={{ marginBottom: 4 }}>
            <RefusalPanel kind="exhausted" hideMonetaryValues={hideMonetaryValues} />
            <button
              onClick={() => void load()}
              style={{ width: '100%', marginTop: 12, padding: '13px', borderRadius: buttonRadius, background: 'transparent', color: 'var(--color-gray-700)', border: '1.5px solid var(--color-gray-300)', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Check again
            </button>
          </div>
        ) : (
          <>
        {/* Lookup method — segmented control (demo StaffLookupInput) */}
        <div style={{ display: 'flex', gap: 6, background: 'var(--color-gray-100)', borderRadius: 12, padding: 4, marginBottom: 16 }}>
          {([['phone', 'Phone number'], ['plate', 'License plate']] as const).map(([m, label]) => (
            <button
              key={m}
              onClick={() => switchMethod(m)}
              disabled={locked}
              style={{
                flex: 1, padding: '11px 0', borderRadius: 9, border: 'none', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
                background: method === m ? '#fff' : 'transparent',
                color: method === m ? '#5C2E70' : 'var(--color-gray-600)',
                boxShadow: method === m ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                transition: 'all .15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={labelStyle}>{method === 'phone' ? 'Phone number' : 'License plate'}</div>
          {method === 'phone' ? (
            <input
              key="phone"
              value={phone}
              onChange={e => { invalidateLookup(); setPhone(fmtPhone(e.target.value)); if (outcome) setOutcome(null) }}
              onKeyDown={e => { if (e.key === 'Enter' && canSubmit) void lookUp() }}
              disabled={locked}
              placeholder="(555) 123-4567"
              type="tel" inputMode="numeric" autoComplete="off"
              style={{ ...bigInput, fontWeight: 700, letterSpacing: '1px', textAlign: 'center', fontSize: 24 }}
              autoFocus
            />
          ) : (
            <input
              key="plate"
              value={plate}
              onChange={e => { invalidateLookup(); setPlate(maskPlate(e.target.value)); if (outcome) setOutcome(null) }}
              onKeyDown={e => { if (e.key === 'Enter' && canSubmit) void lookUp() }}
              disabled={locked}
              placeholder="ABC1234"
              autoCapitalize="characters" autoCorrect="off" autoComplete="off" spellCheck={false}
              style={{ ...bigInput, fontFamily: 'monospace', fontWeight: 700, letterSpacing: '2px', textAlign: 'center', fontSize: 26 }}
              autoFocus
            />
          )}
          <div style={{ fontSize: 12, color: 'var(--color-gray-600)', marginTop: 6, lineHeight: 1.45 }}>
            {method === 'phone'
              ? 'Finds the active parking session linked to this number and applies the validation to it.'
              : 'Finds the active session parked under this plate and applies the validation to it.'}
          </div>
        </div>

        <button
          onClick={() => void lookUp()}
          disabled={!canSubmit}
          style={{
            width: '100%', padding: '16px', borderRadius: 12, background: '#7F3F98', color: '#fff',
            border: 'none', fontWeight: 800, fontSize: 17, cursor: 'pointer', fontFamily: 'inherit',
            opacity: canSubmit ? 1 : 0.5, boxShadow: '0 6px 18px rgba(127,63,152,0.35)',
          }}
        >
          {busy === 'lookup' ? 'Looking up…' : busy === 'confirm' ? 'Applying validation…' : 'Look up & apply validation'}
        </button>
          </>
        )}

        {outcome && (
          <div style={{ marginTop: 22 }}>
            {outcome.kind === 'error'
              ? (outcome.refusal
                ? <RefusalPanel kind={outcome.refusal} hideMonetaryValues={hideMonetaryValues} />
                : <Banner tone={TONE.rejected} icon="✕" title="Couldn't validate">{outcome.message}</Banner>)
              // typedPlate only identifies the car when the PLATE tab did the
              // lookup — a phone result must never inherit a stale plate typed
              // on the other tab (phone hits carry maskedPlate instead).
              // `found` says its piece in the confirm row right below — a
              // banner AND a confirm card repeating each other is two things to
              // read at a counter, so the banner sits this one out.
              // A miss on a fully-free grant is not a failure — it is the
              // walk-up case, and the answer (start their session free) is
              // right below. Leading with an orange "No active parking" makes
              // the good outcome read as recovery from an error, so the
              // free-start block states the situation itself instead. A miss on
              // a PAID discount keeps the banner: there really is nothing to do
              // but text them a link.
              : outcome.result.status === 'no_session' && canStartFree(reviewedOffer) ? null
              : outcome.result.status === 'found' ? null
              : refusalOfResult(outcome.result)
                ? <RefusalPanel kind={refusalOfResult(outcome.result)!} detail={outcome.result.status === 'inactive' ? outcome.result.message : undefined} hideMonetaryValues={hideMonetaryValues} />
              : <ResultBanner result={outcome.result} typedPlate={outcome.lookup.method === 'plate' ? outcome.lookup.plate : ''} />}

            {/* A registered visitor's validation already applied (outcome.auto),
                so say so: nothing is waiting on staff. */}
            {outcome.kind === 'result' && outcome.auto && TONE[outcome.result.status].ok && (
              <div style={{ marginTop: 10, fontSize: 13, fontWeight: 700, color: 'var(--color-gray-700)' }}>
                Applied automatically. No further action needed.
              </div>
            )}

            {/* A `found` that could not be applied automatically (no session
                reference to bind it) — arm the refund by hand, and name the car
                it belongs to. With the auto-apply above, this row is the
                fallback path for the money-moving call. */}
            {result?.status === 'found' && !hideMonetaryValues && (
              <div style={{ marginTop: 12, background: 'var(--color-brand-light, rgba(127,63,152,0.07))', border: '1.5px solid rgba(127,63,152,0.35)', borderRadius: 12, padding: '16px 18px' }}>
                <div style={{ fontSize: 15, color: 'var(--color-black, #231F20)', lineHeight: 1.45, marginBottom: 14 }}>
                  Refund {reviewedOffer ? <b>{discountLabel(reviewedOffer)}</b> : 'the discount'} to the card on{' '}
                  <b style={{ fontFamily: 'monospace', letterSpacing: '1px' }}>{result.maskedPlate ?? result.plate ?? (reviewed?.method === 'phone' ? fmtPhone(reviewed.phone) : reviewed?.plate)}</b>?
                  <div style={{ fontSize: 12, color: 'var(--color-gray-600)', marginTop: 4 }}>This sends money back to the driver's card and can't be undone.</div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => void confirmRefund()}
                    disabled={!!busy}
                    style={{ flex: 2, padding: '15px', borderRadius: 12, background: '#7F3F98', color: '#fff', border: 'none', fontWeight: 800, fontSize: 16, cursor: 'pointer', fontFamily: 'inherit', opacity: busy ? 0.6 : 1, boxShadow: '0 6px 18px rgba(127,63,152,0.35)' }}
                  >
                    {busy === 'confirm' ? 'Refunding…' : 'Confirm refund'}
                  </button>
                  <button
                    onClick={reset}
                    disabled={!!busy}
                    style={{ flex: 1, padding: '15px', borderRadius: 12, background: 'transparent', color: 'var(--color-gray-700)', border: '1.5px solid var(--color-gray-300)', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {result?.status === 'found' && hideMonetaryValues && (
              <Banner tone={TONE.already_free} icon="✓" title="Parking session found">
                This validation-only location does not accept payments, so there is no charge to adjust here.
              </Banner>
            )}

            {/* Phone hit 2+ cars → the fix is one tap away */}
            {result?.status === 'multiple_sessions' && (
              <button
                onClick={() => switchMethod('plate')}
                style={{ width: '100%', marginTop: 12, padding: '13px', borderRadius: buttonRadius, background: '#7F3F98', color: '#fff', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Look up by plate instead
              </button>
            )}

            {/* No session → two moves (slice 4 + follow-up). For fully-free
                grants (full/minutes/freemax) staff can start the $0 session
                right here with the visitor's plate; the registration invite
                stays available (and is the ONLY move for paid discounts —
                flat/percent have a charge to discount, nothing to start). */}
            {result?.status === 'no_session' && reviewed && (
              <NoSessionActions
                token={session.token}
                code={reviewed.code}
                offer={reviewedOffer}
                leading={canStartFree(reviewedOffer)}
                initialPlate={reviewed.method === 'plate' ? reviewed.plate : ''}
                lookupPhone={reviewed.method === 'phone' ? reviewed.phone : ''}
                hideMonetaryValues={hideMonetaryValues}
                onActivity={() => setActivityTick(t => t + 1)}
              />
            )}

            {/* `found` has its own Cancel inside the confirm row — a second
                clear-out button beside it just splits the same action in two. */}
            {(result?.status !== 'found' || hideMonetaryValues) && (
            <button onClick={reset} style={{ width: '100%', marginTop: 12, padding: '13px', borderRadius: buttonRadius, background: 'transparent', color: 'var(--color-gray-700)', border: '1.5px solid var(--color-gray-300)', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
              Validate another customer
            </button>
            )}
          </div>
        )}
      </Card>
    </div>
  )
}

// Tenant-branded kiosk masthead (demo StaffHeader): the tenant's own logo and
// color so staff instantly recognize "our register", with Parkify supplying
// the machinery underneath.
function StaffHeader({ tenant }: { tenant: TenantInfo }) {
  const color = tenant.brandColor || '#7F3F98'
  // A broken/expired logoUrl falls back to the branded initial badge instead
  // of a broken-image icon greeting every walk-up.
  const [logoBroken, setLogoBroken] = useState(false)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid var(--color-gray-200)' }}>
      {tenant.logoUrl && !logoBroken ? (
        // `contain`, never `cover`: cover silently center-crops any logo that
        // isn't square — it ate the ring off circular badge logos here. Uploads
        // are now framed to a square at upload time (LogoAdjuster), so contain
        // fills this circle exactly for those and merely letterboxes (rather
        // than mutilates) the older non-square ones.
        <img
          src={tenant.logoUrl}
          alt={`${tenant.name} logo`}
          onError={() => setLogoBroken(true)}
          style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'contain', background: '#fff', flexShrink: 0, boxShadow: '0 0 0 2px rgba(0,0,0,0.06)' }}
        />
      ) : (
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 20, flexShrink: 0 }}>
          {(tenant.name || '?').charAt(0).toUpperCase()}
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color, letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: 2 }}>Validation station</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--color-black, #231F20)', lineHeight: 1.2 }}>{tenant.name}</div>
      </div>
    </div>
  )
}

export function ResultBanner({ result, typedPlate }: { result: EvalidateResult; typedPlate: string }) {
  const tone = TONE[result.status]
  const title =
    result.status === 'found' ? 'Car found — not refunded yet' :
    result.status === 'validated' ? 'Validated' :
    result.status === 'time_appended' ? 'Parking extended' :
    result.status === 'no_time_added' ? 'No time added' :
    result.status === 'fully_covered' ? 'Already covered' :
    result.status === 'already_validated' ? 'Already validated' :
    result.status === 'no_session' ? 'No active parking' :
    result.status === 'multiple_sessions' ? 'More than one car' :
    result.status === 'already_free' ? 'Already free' :
    result.status === 'rejected' ? 'Not validated' :
    'Inactive'
  // What identifies the car on the banner: the plate staff typed, or the
  // masked tail a phone lookup returns (a shared iPad never learns more).
  const carLabel = result.maskedPlate ?? (result.plate || typedPlate || '')
  const expiry = clockLabel(result.newExpiresAt ?? result.expiresAt)
  return (
    <Banner tone={tone} icon={tone.icon} title={title}>
      {tone.ok && typeof result.refundedCents === 'number' && (
        <div style={{ fontSize: 34, fontWeight: 800, color: tone.color, margin: '2px 0 6px' }}>
          ${(result.refundedCents / 100).toFixed(2)} refunded
        </div>
      )}
      {tone.ok && (carLabel || result.zoneName || expiry) && (
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', marginBottom: 6 }}>
          {[
            carLabel ? `Plate ${carLabel}` : '',
            result.zoneName ?? '',
            expiry ? `parked until ${expiry}` : '',
          ].filter(Boolean).join(' · ')}
        </div>
      )}
      {result.status === 'multiple_sessions' && typeof result.count === 'number' && (
        <div style={{ fontSize: 13, fontWeight: 700, color: tone.color, marginBottom: 6 }}>
          {result.count} active sessions on this number
        </div>
      )}
      <div>{result.message}</div>
      {tone.ok && result.smsSent === true && (
        <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: tone.color }}>
          ✓ Confirmation text sent to the driver
        </div>
      )}
    </Banner>
  )
}

/**
 * The station's answer when validation cannot be applied: the allowance is
 * spent, or validation is unavailable right now. Matches Austin's demo
 * (docs/VALIDATION-PARKIFY-VISUAL-MAP.md): a plain headline, and the line that
 * matters to the person at the counter, which is that the guest is not stuck.
 *
 * The guest-pay line is left off a validation-only lot (`hideMonetaryValues`):
 * it takes no payments, so "park and pay" would promise a door that is not there.
 */
export function RefusalPanel({ kind, detail, hideMonetaryValues }: {
  kind: KioskRefusal
  /** Server wording, shown only where it adds something (an expired program). */
  detail?: string
  hideMonetaryValues: boolean
}) {
  const exhausted = kind === 'exhausted'
  return (
    <div role="alert">
      <Banner tone={TONE.no_session} icon="!" title={exhausted ? 'No validations remaining' : "Validation isn't available right now"}>
        {exhausted && <div>Validation isn&apos;t available here right now.</div>}
        {detail && <div>{detail}</div>}
        {!hideMonetaryValues && <div style={{ fontWeight: 700 }}>Guests can still park and pay for themselves.</div>}
      </Banner>
    </div>
  )
}

function ScheduleNotice({ offer }: { offer: Validation }) {
  return (
    <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: 'var(--color-gray-700)' }}>
      {offer.activeHours ? `Available: ${formatValidationHours(offer.activeHours)}` : formatValidationHours(offer.activeHours)}
      {offer.activeHours && <span style={{ fontWeight: 600, color: 'var(--color-gray-600)' }}> · zone local time</span>}
    </div>
  )
}

// Which grants can be started as a $0 session at the counter. Mirrors the
// server's type gate (validations/free_start.ts): flat/percent discount a
// CHARGE, so there is nothing to start for free. A capped `freemax` passes here
// and may still be refused server-side when the cap binds — that refusal names
// the invite as the next move, which is why the invite block stays mounted.
const FREE_START_TYPES = new Set(['full', 'minutes', 'freemax'])
export const canStartFree = (offer: Validation | null): boolean =>
  !!offer && FREE_START_TYPES.has(offer.type)

// Slice-4 follow-up — what staff can do when the lookup found NO session.
// Fully-free grants (full / minutes / freemax) get the demo's staff-initiated
// free session start; the registration invite stays underneath as the other
// path (and is the only one for paid discounts). A successful start replaces
// both affordances with the session card — there's nothing left to invite.
function NoSessionActions({ token, code, offer, initialPlate, lookupPhone, leading, hideMonetaryValues, onActivity }: {
  token: string
  code: string
  offer: Validation | null
  initialPlate: string
  lookupPhone: string
  /** The free start is the headline (no miss banner above it) — see the render
      site. Both blocks reword so the pair reads as one choice, not two alerts. */
  leading: boolean
  hideMonetaryValues: boolean
  onActivity: () => void
}) {
  const [started, setStarted] = useState<KioskFreeStartResult | null>(null)
  const eligible = canStartFree(offer)
  if (started) return <FreeStartSuccess result={started} sentTo={lookupPhone} />
  return (
    <>
      {eligible && (
        <FreeStartBlock
          token={token}
          code={code}
          offer={offer!}
          initialPlate={initialPlate}
          lookupPhone={lookupPhone}
          leading={leading}
          hideMonetaryValues={hideMonetaryValues}
          onStarted={setStarted}
          onActivity={onActivity}
        />
      )}
      <InviteBlock token={token} code={code} arrivalUrl={offer?.arrivalUrl} secondary={eligible && leading}
        initialPhone={lookupPhone ? fmtPhone(lookupPhone) : ''} onActivity={onActivity} />
    </>
  )
}

// Collect the plate (prefilled when the plate tab did the lookup) and start
// the $0 session — confirm-armed like the money lookups: the first tap arms,
// only Confirm creates. The idempotencyKey is minted when the confirm ARMS, so
// a double-tap of the same confirm replays as the same session server-side.
function FreeStartBlock({ token, code, offer, initialPlate, lookupPhone, leading, hideMonetaryValues, onStarted, onActivity }: {
  token: string
  code: string
  offer: Validation
  initialPlate: string
  lookupPhone: string
  /** Carrying the "they aren't parked" fact itself, because no banner does. */
  leading: boolean
  hideMonetaryValues: boolean
  onStarted: (r: KioskFreeStartResult) => void
  onActivity: () => void
}) {
  const [plate, setPlate] = useState(initialPlate)
  const [confirming, setConfirming] = useState(false)
  const [idemKey, setIdemKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<KioskFreeStartResult | null>(null)
  const [refused, setRefused] = useState<KioskRefusal | null>(null)
  const [errMsg, setErrMsg] = useState('')
  const ready = plate.length >= 2 && !busy

  // One key per PLATE, not per arm: a timeout/network retry of the same start
  // must replay as the SAME request server-side (same derived txnId → same
  // session), not read back as already_parked. Editing the plate clears it.
  const arm = () => {
    if (!ready) return
    if (!idemKey) setIdemKey(crypto.randomUUID())
    setConfirming(true)
    onActivity()
  }

  const start = async () => {
    if (!ready || !idemKey) return
    setBusy(true); setErrMsg(''); setRefused(null)
    try {
      const r = await startFreeSession(token, code, {
        plate,
        idempotencyKey: idemKey,
        ...(lookupPhone.length === 10 ? { phone: lookupPhone } : {}),
      })
      if (r.status === 'started') onStarted(r)
      else setNotice(r)
    } catch (e) {
      const refusal = refusalOfError(e)
      if (refusal) setRefused(refusal)
      else setErrMsg(e instanceof Error ? e.message : "Something went wrong. The session wasn't started — try again.")
    } finally {
      setBusy(false); setConfirming(false)
    }
  }

  return (
    <div style={{ marginTop: 12, background: 'var(--color-brand-light, rgba(127,63,152,0.07))', border: `1.5px solid rgba(127,63,152,${leading ? 0.4 : 0.25})`, borderRadius: 12, padding: leading ? '16px 18px' : '14px 16px' }}>
      <div style={{ fontSize: leading ? 16 : 14, fontWeight: 800, color: 'var(--color-black, #231F20)', marginBottom: 4 }}>
        {leading ? "Not parked yet — start their free session" : 'Start their free session'}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--color-gray-600)', lineHeight: 1.5, marginBottom: 12 }}>
        {validationDisplayLabel(offer, hideMonetaryValues)} — your validation covers it, so there's no charge and nothing for the driver to do.
        We just need their plate.
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: confirming || notice || refused || errMsg ? 12 : 0 }}>
        <input
          value={plate}
          onChange={e => { setPlate(maskPlate(e.target.value)); setIdemKey(''); setConfirming(false); if (notice) setNotice(null); if (refused) setRefused(null); if (errMsg) setErrMsg(''); onActivity() }}
          onKeyDown={e => { if (e.key === 'Enter' && ready) arm() }}
          placeholder="ABC1234"
          autoCapitalize="characters" autoCorrect="off" autoComplete="off" spellCheck={false}
          style={{ flex: 1, padding: '12px 14px', borderRadius: 10, border: '1.5px solid var(--color-gray-300)', fontFamily: 'monospace', fontSize: 17, fontWeight: 700, letterSpacing: '1.5px', background: '#fff', boxSizing: 'border-box', minWidth: 0 }}
        />
        {!confirming && (
          <button
            onClick={arm}
            disabled={!ready}
            style={{ padding: '0 18px', borderRadius: buttonRadius, background: '#7F3F98', color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: ready ? 'pointer' : 'not-allowed', fontFamily: 'inherit', opacity: ready ? 1 : 0.55, whiteSpace: 'nowrap' }}
          >
            Start free session
          </button>
        )}
      </div>
      {confirming && (
        <div style={{ background: '#fff', border: '1.5px solid rgba(127,63,152,0.35)', borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 14, color: 'var(--color-black, #231F20)', lineHeight: 1.45, marginBottom: 10 }}>
            Start a free session for <b style={{ fontFamily: 'monospace', letterSpacing: '1px' }}>{plate}</b>?
            <div style={{ fontSize: 12, color: 'var(--color-gray-600)', marginTop: 3 }}>
              {validationDisplayLabel(offer, hideMonetaryValues)} covers it — no charge to the driver.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => void start()}
              disabled={!ready}
              style={{ flex: 2, padding: '12px', borderRadius: buttonRadius, background: '#7F3F98', color: '#fff', border: 'none', fontWeight: 800, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', opacity: ready ? 1 : 0.6 }}
            >
              {busy ? 'Starting…' : 'Confirm — start session'}
            </button>
            <button
              onClick={() => setConfirming(false)}
              disabled={busy}
              style={{ flex: 1, padding: '12px', borderRadius: buttonRadius, background: 'transparent', color: 'var(--color-gray-700)', border: '1.5px solid var(--color-gray-300)', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {(refused || (notice && refusalOfResult(notice))) && (
        <RefusalPanel kind={(refused ?? refusalOfResult(notice!))!} hideMonetaryValues={hideMonetaryValues} />
      )}
      {notice && !refusalOfResult(notice) && (
        // Server said no (already parked / cap binds / not sellable / limits) —
        // its message is staff-actionable and, where it applies, points at the
        // invite block rendered right below this one.
        <div style={{ background: 'rgba(241,101,33,0.10)', border: '1.5px solid rgba(241,101,33,0.4)', borderRadius: 10, padding: '11px 14px', fontSize: 13, fontWeight: 600, color: '#b45309', lineHeight: 1.5 }}>
          {notice.message}
        </div>
      )}
      {errMsg && (
        <div style={{ fontSize: 12.5, fontWeight: 600, color: '#b91c1c', lineHeight: 1.5 }}>{errMsg}</div>
      )}
    </div>
  )
}

// The demo's FreeSessionStart success beat: session line (masked plate · zone ·
// parked until) + the HONEST text beat — smsSent true/false comes straight from
// the server's actual send, and no phone collected means no claim at all.
function FreeStartSuccess({ result, sentTo }: { result: KioskFreeStartResult; sentTo: string }) {
  const expiry = clockLabel(result.expiresAt)
  return (
    <div style={{ marginTop: 12 }}>
      <Banner tone={TONE.validated} icon="✓" title="Free session started">
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', marginBottom: 6 }}>
          {[
            result.maskedPlate ? `Plate ${result.maskedPlate}` : '',
            result.zoneName ?? '',
            expiry ? `parked until ${expiry}` : '',
          ].filter(Boolean).join(' · ')}
        </div>
        <div>No payment needed — {result.businessName ? `${result.businessName} is covering it` : 'the validation covers it'}.</div>
        {result.smsSent === true && (
          <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: TONE.validated.color }}>
            ✓ Confirmation text sent{sentTo ? ` to ${fmtPhone(sentTo)}` : ' to the driver'}
          </div>
        )}
        {result.smsSent === false && (
          <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: '#b45309' }}>
            We couldn't send the confirmation text — the session is still active.
          </div>
        )}
      </Banner>
    </div>
  )
}

// Slice 4 — registration invite. Rendered under a no_session result: the
// visitor isn't parked (or paid) yet, so there's nothing to refund — the
// validation reaches them by scanning the posted in-store code, or by a text
// that lands in checkout with it pre-applied.
function InviteBlock({ token, code, arrivalUrl, initialPhone, secondary, onActivity }: {
  token: string
  code: string
  /** From the server — see `Validation.arrivalUrl`. Absent ⇒ no QR to show. */
  arrivalUrl?: string
  initialPhone: string
  /** A free start is offered above, so this is the alternative rather than the
      only move — the heading must not ask "not parked yet?" a second time. */
  secondary: boolean
  onActivity: () => void
}) {
  const [phone, setPhone] = useState(initialPhone)
  const [qr, setQr] = useState('')
  const [busy, setBusy] = useState(false)
  const [sentMsg, setSentMsg] = useState('')
  const [refused, setRefused] = useState<KioskRefusal | null>(null)
  const [errMsg, setErrMsg] = useState('')
  const digits = phone.replace(/\D/g, '')
  const ready = digits.length === 10 && !busy

  // Rendered from the SERVER's url, never a locally-composed one. Failure is
  // silent on purpose: no QR is a complete state (see `arrivalUrl`), and the
  // text-a-link path below is unaffected, so there is nothing to apologise for.
  //
  // ⚠️ DELIBERATELY IGNORES `Validation.customQrUrl`, unlike the Validation
  // screen's OfferQrActions — this is not an oversight, don't "fix" it. That
  // card is the tenant looking at their own signage; THIS code is handed to a
  // named visitor mid-transaction under the promise below that it "applies
  // automatically". The uploaded artwork is display-only and is not verified to
  // carry the `code` param, so substituting it here would recreate exactly the
  // silent full-price failure the copy comment further down was written about.
  // Only the generated code is guaranteed to pre-apply the validation.
  useEffect(() => {
    if (!arrivalUrl) { setQr(''); return }
    let live = true
    void generateBrandedQrSvg(arrivalUrl).then(svg => { if (live) setQr(svg) }).catch(() => {})
    return () => { live = false }
  }, [arrivalUrl])

  const send = async () => {
    if (!ready) return
    setBusy(true); setErrMsg(''); setRefused(null)
    try {
      const r = await sendInvite(token, code, digits)
      const refusal = r.ok ? null : refusalOfResult(r)
      if (refusal) setRefused(refusal)
      else if (r.ok) setSentMsg(r.message || 'Invite sent.')
      else setErrMsg(r.message || "Couldn't send the invite.")
    } catch (e) {
      const refusal = refusalOfError(e)
      if (refusal) setRefused(refusal)
      else setErrMsg(e instanceof Error ? e.message : "Couldn't send the invite.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ marginTop: 12, background: 'var(--color-gray-100)', borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--color-black, #231F20)', marginBottom: 4 }}>
        {secondary ? 'Or send them the validation instead' : 'Not parked yet? Send them the validation'}
      </div>
      {/* ⚠️ This copy used to read "They can scan your posted Parkify code, or we
          can text a link — the validation applies automatically when they start
          their session." One promise, after two options, true of only ONE of
          them: the posted sign encodes the ZONE and no `code`, so scanning it
          starts a session and applies nothing. A clerk read that, said "just
          scan the sign", and the visitor paid full price while the tenant
          believed they'd given a validation — and nobody found out at the
          counter. Both options below now carry this code, so the promise is
          finally true of both. */}
      <div style={{ fontSize: 12.5, color: 'var(--color-gray-600)', lineHeight: 1.5, marginBottom: 12 }}>
        {qr
          ? <>Have them scan this code, or we can text the same link — either way the validation
            applies automatically when they start their session.{' '}
            <b>Your posted sign won’t apply it</b> — it starts a session without this validation.</>
          : <>We can text them a link — the validation applies automatically when they start their
            session.{' '}<b>Your posted sign won’t apply it</b> — it starts a session without
            this validation.</>}
      </div>
      {qr && (
        /* Sits ABOVE the phone field: a visitor standing at the counter should
           scan rather than read their number out, which is also the option that
           costs no billed SMS. */
        <div style={{ display: 'flex', justifyContent: 'center', background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: 14, marginBottom: 12 }}>
          <div style={{ width: 132, height: 132 }} dangerouslySetInnerHTML={{ __html: qr }} />
        </div>
      )}
      {sentMsg ? (
        <div style={{ background: 'rgba(98,197,132,0.14)', border: '1.5px solid rgba(98,197,132,0.5)', borderRadius: 10, padding: '11px 14px', fontSize: 13, fontWeight: 700, color: '#15803d' }}>
          ✓ {sentMsg}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={phone}
              onChange={e => { setPhone(fmtPhone(e.target.value)); if (errMsg) setErrMsg(''); if (refused) setRefused(null); onActivity() }}
              onKeyDown={e => { if (e.key === 'Enter' && ready) void send() }}
              placeholder="(555) 123-4567"
              type="tel" inputMode="numeric" autoComplete="off"
              style={{ flex: 1, padding: '12px 14px', borderRadius: 10, border: '1.5px solid var(--color-gray-300)', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, background: '#fff', boxSizing: 'border-box', minWidth: 0 }}
            />
            <button
              onClick={() => void send()}
              disabled={!ready}
              style={{ padding: '0 18px', borderRadius: buttonRadius, background: '#7F3F98', color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: ready ? 'pointer' : 'not-allowed', fontFamily: 'inherit', opacity: ready ? 1 : 0.55, whiteSpace: 'nowrap' }}
            >
              {busy ? 'Sending…' : 'Text the link'}
            </button>
          </div>
          {refused && <div style={{ marginTop: 8 }}><RefusalPanel kind={refused} hideMonetaryValues={false} /></div>}
          {errMsg && (
            <div style={{ marginTop: 8, fontSize: 12.5, fontWeight: 600, color: '#b91c1c' }}>{errMsg}</div>
          )}
        </>
      )}
    </div>
  )
}

function Banner({ tone, icon, title, children }: {
  tone: { bg: string; border: string; color: string }
  icon: string
  title: string
  children: React.ReactNode
}) {
  return (
    <div style={{ background: tone.bg, border: `1.5px solid ${tone.border}`, borderRadius: 14, padding: '18px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <span style={{ width: 26, height: 26, borderRadius: '50%', background: tone.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: icon.length > 1 ? 11 : 15, flexShrink: 0 }}>{icon}</span>
        <span style={{ fontSize: 16, fontWeight: 800, color: tone.color }}>{title}</span>
      </div>
      <div style={{ fontSize: 14, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>{children}</div>
    </div>
  )
}

// Inline load-failure block — shown in place of the empty state when the offer
// list fails to load, so a front-desk worker never reads "couldn't load" as "no
// offers" and goes off to create one. Same red banner tone as TONE.rejected;
// Retry re-runs the loader. Mirrors the LoadError in Dashboard / History /
// Integrations for cross-screen consistency.
function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div style={{ background: 'rgba(237,28,36,0.10)', border: '1.5px solid rgba(237,28,36,0.4)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#b91c1c', marginBottom: 2 }}>Couldn't load</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>{message}</div>
      </div>
      <button onClick={onRetry} style={{ padding: '8px 18px', borderRadius: buttonRadius, background: '#7F3F98', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Retry</button>
    </div>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid var(--color-gray-200)', padding: '28px 28px 30px', boxShadow: '0 4px 20px rgba(35,31,32,0.06)' }}>
      {children}
    </div>
  )
}

/** "180" → "3 hours", "45" → "45 min" — only collapses to hours on an even multiple of 60, so an odd duration never renders as a fraction. */
function freeDuration(minutes: number): string {
  if (minutes >= 60 && minutes % 60 === 0) {
    const hours = minutes / 60
    return `${hours} hour${hours > 1 ? 's' : ''}`
  }
  return `${minutes} min`
}

export function discountLabel(v: Validation): string {
  switch (v.type) {
    case 'full': return '100% off'
    case 'flat': return `$${(v.value / 100).toFixed(2)} off`
    case 'percent': return `${v.value}% off`
    case 'minutes': return `${freeDuration(v.value)} free`
    case 'freemax': return `${freeDuration(v.value)} free${typeof v.capCents === 'number' ? `, up to $${(v.capCents / 100).toFixed(2)} off` : ''}`
    default: return ''
  }
}

/** The same benefit without a dollar/percentage claim for an unpaid lot. */
export function validationDisplayLabel(v: Validation, hideMonetaryValues = false): string {
  if (!hideMonetaryValues) return discountLabel(v)
  switch (v.type) {
    case 'minutes':
    case 'freemax':
      return `${freeDuration(v.value)} parking`
    case 'until':
      return v.untilTime ? `Parking through ${v.untilTime}` : 'Parking validation'
    default:
      return 'Parking validation'
  }
}

const bigInput: CSSProperties = {
  width: '100%', padding: '14px 16px', borderRadius: 12, border: '1.5px solid var(--color-gray-300)',
  fontFamily: 'inherit', fontSize: 18, background: '#fff', boxSizing: 'border-box',
}
